import React from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import CascadeIcon from './CascadeIcon';

const reasonMessages: Record<string, { title: string; message: string }> = {
  config_error: {
    title: 'Authentication Unavailable',
    message: 'Authentication is not available right now. Please contact your administrator.',
  },
  auth_failed: {
    title: 'Authentication Failed',
    message: 'We were unable to sign you in. Please try again.',
  },
  access_denied: {
    title: 'Access Denied',
    message: "You don't have permission to access this application.",
  },
};

const defaultMessage = {
  title: 'Sign-In Error',
  message: 'Something went wrong during sign-in. Please try again.',
};

const AuthError: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason') ?? '';
  const { title, message } = reasonMessages[reason] ?? defaultMessage;

  const handleRetry = () => {
    window.location.href = '/login';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
        padding: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          width: '100%',
          maxWidth: 440,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CascadeIcon
            sx={{
              fontSize: 64,
              color: 'primary.main',
              mb: 1,
            }}
          />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Cascade
          </Typography>
        </Box>

        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {message}
          </Typography>
        </Alert>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleRetry}
          sx={{ mb: 2 }}
        >
          Try Again
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            If this problem persists, please contact your administrator.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AuthError;
